import { useState, useRef, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Users,
  Settings,
  Upload,
  UserPlus,
  Edit,
  Trash2,
  Search,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface UserRow {
  id: number
  email: string
  isAdmin: boolean
  allowedUserTypes?: string[]
  editUrl: string
  deleteUrl: string
}

interface Props extends Omit<AppShellProps, 'children'> {
  account: {
    id: number
    subdomainName: string
    logoUrl: string | null
    ownerEmail: string
    metaWhatsappPhoneNumberId?: string | null
    metaWhatsappAccessTokenConfigured?: boolean
    metaWhatsappBusinessId?: string | null
    metaWhatsappVerifyTokenConfigured?: boolean
    metaWhatsappAppId?: string | null
    metaWhatsappAppSecretConfigured?: boolean
  }
  users: UserRow[]
  twoFactor: {
    enabled: boolean
    method: 'app' | 'email'
    setupUrl: string
    disableUrl: string
  }
  urls: AppShellProps['urls'] & {
    updateAccount: string
    inviteUser: string
    whatsappOauthUrl?: string
  }
  translations: AppShellProps['translations'] & {
    accountSettings: string
    allBadgeTypes?: string
    badgeTypes?: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Setting(props: Props) {
  const { account, users, twoFactor, urls, translations: t, ...shell } = props
  const [tab, setTab] = useState<'settings' | 'users'>('settings')
  const [logo, setLogo] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const handleLogoUpload = (e: FormEvent) => {
    e.preventDefault()
    if (!logo) return
    setUploading(true)
    const fd = new FormData()
    fd.append('account[logo]', logo)
    router.put(urls.updateAccount, fd as any, {
      forceFormData: true,
      onFinish: () => setUploading(false),
    })
  }

  const [waData, setWaData] = useState({
    meta_whatsapp_phone_number_id: account.metaWhatsappPhoneNumberId || '',
    meta_whatsapp_access_token: '',
    meta_whatsapp_business_id: account.metaWhatsappBusinessId || '',
    meta_whatsapp_verify_token: '',
    meta_whatsapp_app_id: account.metaWhatsappAppId || '',
    meta_whatsapp_app_secret: '',
  })
  const [savingWa, setSavingWa] = useState(false)

  const handleSaveWa = (e: FormEvent) => {
    e.preventDefault()
    setSavingWa(true)
    const fd = new FormData()
    fd.append('account[meta_whatsapp_phone_number_id]', waData.meta_whatsapp_phone_number_id)
    fd.append('account[meta_whatsapp_business_id]', waData.meta_whatsapp_business_id)
    fd.append('account[meta_whatsapp_app_id]', waData.meta_whatsapp_app_id)
    if (waData.meta_whatsapp_access_token)
      fd.append('account[meta_whatsapp_access_token]', waData.meta_whatsapp_access_token)
    if (waData.meta_whatsapp_verify_token)
      fd.append('account[meta_whatsapp_verify_token]', waData.meta_whatsapp_verify_token)
    if (waData.meta_whatsapp_app_secret)
      fd.append('account[meta_whatsapp_app_secret]', waData.meta_whatsapp_app_secret)
    router.put(urls.updateAccount, fd as any, {
      preserveScroll: true,
      forceFormData: true,
      onFinish: () => setSavingWa(false),
    })
  }

  const handleDeleteUser = (user: UserRow) => {
    if (!confirm(`Delete user ${user.email}?`)) return
    router.delete(user.deleteUrl)
  }

  const filteredUsers = users.filter((u) =>
    u.email.toLowerCase().includes(userSearch.toLowerCase()),
  )

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.accountSettings} />

      <div className="mx-auto max-w-4xl space-y-6 py-8 px-4">
        <h1 className="text-xl font-semibold text-[#37352F]">{t.accountSettings}</h1>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E8E8E5]">
          <button
            onClick={() => setTab('settings')}
            className="flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              borderColor: tab === 'settings' ? '#2383E2' : 'transparent',
              color: tab === 'settings' ? '#2383E2' : '#91918E',
            }}
          >
            <Settings className="h-4 w-4" /> Settings
          </button>
          <button
            onClick={() => setTab('users')}
            className="flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              borderColor: tab === 'users' ? '#2383E2' : 'transparent',
              color: tab === 'users' ? '#2383E2' : '#91918E',
            }}
          >
            <Users className="h-4 w-4" /> Users
          </button>
        </div>

        {/* Settings Tab */}
        {tab === 'settings' && (
          <div className="space-y-6">
            {/* Account Info */}
            <div className="rounded-lg border border-[#E8E8E5] p-6">
              <h2 className="mb-4 text-lg font-semibold text-[#37352F]">Main Settings</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-[#91918E]">Account Owner</Label>
                  <Input
                    value={account.ownerEmail || ''}
                    readOnly
                    className="mt-1 bg-[#F1F1EF] border-[#E8E8E5] text-[#37352F]"
                  />
                </div>
                <div>
                  <Label className="text-sm text-[#91918E]">Subdomain</Label>
                  <Input
                    value={account.subdomainName || ''}
                    readOnly
                    className="mt-1 bg-[#F1F1EF] border-[#E8E8E5] text-[#37352F]"
                  />
                </div>

                {/* Logo upload */}
                <form onSubmit={handleLogoUpload} className="space-y-3">
                  <Label className="text-sm text-[#91918E]">Logo</Label>
                  {account.logoUrl && (
                    <div className="mb-2">
                      <img
                        src={account.logoUrl}
                        alt="Account logo"
                        className="h-20 rounded border border-[#E8E8E5] object-contain"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => setLogo(e.target.files?.[0] || null)}
                      className="text-sm"
                    />
                    {logo && (
                      <Button
                        type="submit"
                        size="sm"
                        disabled={uploading}
                        className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                      >
                        <Upload className="mr-1 h-3 w-3" />
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Two-Factor Auth */}
            <div className="rounded-lg border border-[#E8E8E5] p-6">
              <h2 className="mb-4 text-lg font-semibold text-[#37352F]">
                Two-Factor Authentication
              </h2>
              {twoFactor.enabled ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <ShieldCheck className="h-5 w-5" />
                    <p className="text-sm">
                      2FA is enabled via{' '}
                      {twoFactor.method === 'app' ? 'Authenticator App' : 'Email'}.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      if (confirm('Are you sure you want to disable 2FA?'))
                        router.delete(twoFactor.disableUrl)
                    }}
                  >
                    <ShieldOff className="mr-2 h-4 w-4" /> Disable 2FA
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-[#91918E]">
                    <Shield className="h-5 w-5" />
                    <p className="text-sm">2FA is not enabled.</p>
                  </div>
                  <Button
                    className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                    onClick={() => router.visit(twoFactor.setupUrl)}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" /> Setup 2FA
                  </Button>
                </div>
              )}
            </div>

            {/* Meta WhatsApp Integration */}
            <div className="rounded-lg border border-[#E8E8E5] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-[#37352F]">Meta WhatsApp Configuration</h2>
                  <p className="text-sm text-[#91918E] mt-0.5">Connect securely via Meta for auto-configuration, or manually provide your Cloud API credentials.</p>
                </div>
                {urls.whatsappOauthUrl && (
                  <a
                    href={urls.whatsappOauthUrl}
                    className="inline-flex items-center justify-center shrink-0 rounded-md bg-[#1877F2] px-4 py-2 text-sm font-medium text-white hover:bg-[#166FE5] transition-colors shadow-sm"
                  >
                    <svg className="mr-2 h-4 w-4 fill-white" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    Connect with Meta
                  </a>
                )}
              </div>
              
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-[#E8E8E5]" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-2 text-xs text-[#91918E] uppercase font-medium tracking-wider">Or Manual Configuration</span>
                </div>
              </div>

              <form onSubmit={handleSaveWa} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm text-[#91918E]">Phone Number ID</Label>
                    <Input
                      value={waData.meta_whatsapp_phone_number_id}
                      onChange={(e) => setWaData({ ...waData, meta_whatsapp_phone_number_id: e.target.value })}
                      className="mt-1 border-[#E8E8E5] text-[#37352F]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#91918E]">WhatsApp Business Account ID</Label>
                    <Input
                      value={waData.meta_whatsapp_business_id}
                      onChange={(e) => setWaData({ ...waData, meta_whatsapp_business_id: e.target.value })}
                      className="mt-1 border-[#E8E8E5] text-[#37352F]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#91918E]">Permanent Access Token</Label>
                  <Input
                    type="password"
                    value={waData.meta_whatsapp_access_token}
                    placeholder={account.metaWhatsappAccessTokenConfigured ? 'Configured — enter a new value to rotate' : ''}
                    onChange={(e) => setWaData({ ...waData, meta_whatsapp_access_token: e.target.value })}
                    className="mt-1 border-[#E8E8E5] text-[#37352F]"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm text-[#91918E]">App ID</Label>
                    <Input
                      value={waData.meta_whatsapp_app_id}
                      onChange={(e) => setWaData({ ...waData, meta_whatsapp_app_id: e.target.value })}
                      className="mt-1 border-[#E8E8E5] text-[#37352F]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#91918E]">App Secret</Label>
                    <Input
                      type="password"
                      value={waData.meta_whatsapp_app_secret}
                      placeholder={account.metaWhatsappAppSecretConfigured ? 'Configured — enter a new value to rotate' : ''}
                      onChange={(e) => setWaData({ ...waData, meta_whatsapp_app_secret: e.target.value })}
                      className="mt-1 border-[#E8E8E5] text-[#37352F]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#91918E]">Webhook Verify Token (Optional)</Label>
                  <Input
                    value={waData.meta_whatsapp_verify_token}
                    placeholder={account.metaWhatsappVerifyTokenConfigured ? 'Configured — enter a new value to rotate' : ''}
                    onChange={(e) => setWaData({ ...waData, meta_whatsapp_verify_token: e.target.value })}
                    className="mt-1 border-[#E8E8E5] text-[#37352F]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={savingWa}
                  className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                >
                  {savingWa ? 'Saving...' : 'Save WhatsApp Settings'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative max-w-xs flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91918E]" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10 border-[#E8E8E5]"
                />
              </div>
              <Button
                className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                onClick={() => router.visit(urls.inviteUser)}
              >
                <UserPlus className="mr-2 h-4 w-4" /> Invite User
              </Button>
            </div>

            <div className="overflow-hidden rounded-lg border border-[#E8E8E5]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F1F1EF]">
                    <th className="px-4 py-3 text-left font-medium text-[#91918E]">Email</th>
                    <th className="px-4 py-3 text-center font-medium text-[#91918E]">Admin</th>
                    <th className="px-4 py-3 text-left font-medium text-[#91918E]">{t.badgeTypes || 'Badge Types'}</th>
                    <th className="px-4 py-3 text-center font-medium text-[#91918E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-[#91918E]">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-t border-[#E8E8E5] transition-colors hover:bg-[#F1F1EF]"
                      >
                        <td className="px-4 py-3 text-[#37352F]">{user.email}</td>
                        <td className="px-4 py-3 text-center">
                          {user.isAdmin ? (
                            <span className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-[#91918E]">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#37352F]">
                          {user.isAdmin || !user.allowedUserTypes?.length
                            ? (t.allBadgeTypes || 'All')
                            : user.allowedUserTypes.join(', ')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => router.visit(user.editUrl)}
                              className="rounded p-1.5 text-[#91918E] hover:bg-[#F1F1EF]"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="rounded p-1.5 text-red-500 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
